import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { RefreshCw } from "lucide-react";
import { API } from "@/lib/api";

const LAST_KEY = "er-owner-last-seen";

const timeAgo = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  return new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
};

// "Anything change while I was away?" — trips touched since this device last looked.
// Per-device by nature (localStorage), so the computer sees what the phone did and vice versa.
export const ChangesPopup = ({ token }) => {
  const [changes, setChanges] = useState(null);

  useEffect(() => {
    if (!token) return;
    const lastSeen = localStorage.getItem(LAST_KEY);
    axios
      .get(`${API}/owner/trips`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const trips = res.data.trips || [];
        if (!lastSeen) {
          localStorage.setItem(LAST_KEY, new Date().toISOString()); // first visit sets the baseline
          setChanges([]);
          return;
        }
        setChanges(
          trips.filter((t) => t.updated_at && t.updated_at > lastSeen).sort((a, b) => b.updated_at.localeCompare(a.updated_at))
        );
      })
      .catch(() => setChanges([]));
  }, [token]);

  const dismiss = () => {
    localStorage.setItem(LAST_KEY, new Date().toISOString());
    setChanges([]);
  };

  if (!changes || changes.length === 0) return null;

  return (
    <AnimatePresence>
      <motion.div
        data-testid="changes-popup"
        className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="w-full max-w-sm border border-amber-500/30 bg-[#0c0c10] p-6"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">
            <RefreshCw className="h-3.5 w-3.5" /> Since you last checked here
          </p>
          <p className="mt-1.5 text-xs text-neutral-500">
            {changes.length === 1 ? "1 journey was" : `${changes.length} journeys were`} touched on another device.
          </p>
          <div className="mt-4 max-h-64 space-y-2 overflow-y-auto">
            {changes.map((t) => (
              <div key={t.id} data-testid={`changes-row-${t.id.slice(0, 6)}`} className="flex items-center justify-between gap-3 border border-neutral-800 px-3 py-2.5">
                <span className="font-serif text-sm text-neutral-100">{t.client?.name || "Untitled journey"}</span>
                <span className="shrink-0 text-[9px] uppercase tracking-[0.18em] text-neutral-500">{timeAgo(t.updated_at)}</span>
              </div>
            ))}
          </div>
          <button
            data-testid="changes-dismiss"
            onClick={dismiss}
            className="mt-5 w-full bg-amber-500 px-4 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-300"
          >
            Got it
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
